import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Title,
  Textarea,
  Select,
  Button,
  Stack,
  Group,
  Alert,
  Text,
  Card,
  Checkbox,
  TextInput,
  NumberInput,
  ActionIcon,
  Divider,
  Badge,
  LoadingOverlay,
} from "@mantine/core";
import { DatePickerInput, TimeInput } from "@mantine/dates";
import { IconArrowLeft, IconAlertCircle, IconPlus, IconTrash, IconGripVertical } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTenderById, useUpdateTender } from "../hooks/useTenders";
import { useTenderItems } from "../hooks/useTenderItems";
import { useTenderRequirements } from "../hooks/useTenderRequirements";
import { useEvaluationCriteria, useCreateEvaluationCriteria, useUpdateEvaluationCriteria, useRemoveEvaluationCriteria } from "../hooks/useEvaluationCriteria";
import { notifications } from "@mantine/notifications";
// Removed unused imports

interface DocumentRequirement {
  id: string;
  documentType: string;
  documentCategory: 'FINANCIAL' | 'GENERAL' | 'TECHNICAL';
  description: string;
  isRequired: boolean;
  order: number;
}

interface TenderItemForm {
  id: string;
  itemName: string;
  description: string;
  quantity: number;
  unit: string;
  order: number;
}

interface EvaluationCriteria {
  id: string;
  criteriaName: string;
  description: string;
  criteriaType: 'FINANCIAL' | 'GENERAL';
  weightage: number;
  order: number;
  isDefault: boolean;
}

const DEFAULT_DOCUMENT_TYPES = [
  { value: 'QUOTATION', label: 'Quotation' },
  { value: 'COMPANY_PROFILE', label: 'Company Profile / Information' },
  { value: 'REGISTRATION_CERTIFICATE', label: 'Company Registration Certificate' },
  { value: 'GST_REGISTRATION', label: 'GST Registration' },
  { value: 'EXPERIENCE_LETTERS', label: 'Experience Letters' },
  { value: 'TOR', label: 'Terms of Reference' },
  { value: 'FINANCIAL_STATEMENT', label: 'Financial Statement' },
  { value: 'TAX_CLEARANCE', label: 'Tax Clearance' },
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'TECHNICAL_PROPOSAL', label: 'Technical Proposal' },
  { value: 'FINANCIAL_PROPOSAL', label: 'Financial Proposal' },
  { value: 'CUSTOM', label: 'Custom Document' },
];

const DEFAULT_UNITS = [
  { value: 'units', label: 'Units' },
  { value: 'pieces', label: 'Pieces' },
  { value: 'kg', label: 'Kilograms' },
  { value: 'liters', label: 'Liters' },
  { value: 'hours', label: 'Hours' },
  { value: 'days', label: 'Days' },
  { value: 'meters', label: 'Meters' },
  { value: 'sets', label: 'Sets' },
  { value: 'pairs', label: 'Pairs' },
  { value: 'boxes', label: 'Boxes' },
];

export function TenderEdit() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const updateTender = useUpdateTender();

  // Fetch tender data
  const { data: tender, isLoading: loadingTender, error: tenderError } = useTenderById(id!);
  
  // Fetch tender items
  const { data: existingItems, isLoading: loadingItems } = useTenderItems(id!);
  
  // Fetch tender requirements
  const { data: existingRequirements, isLoading: loadingRequirements } = useTenderRequirements(id!);
  
  // Fetch evaluation criteria
  const { data: existingCriteria, isLoading: loadingCriteria } = useEvaluationCriteria(id!);
  
  // Mutations for criteria management
  const createCriteria = useCreateEvaluationCriteria();
  const updateCriteria = useUpdateEvaluationCriteria();
  const removeCriteria = useRemoveEvaluationCriteria();

  const [formData, setFormData] = useState({
    description: "",
    category: "",
    publishingDate: new Date(),
    publishingTime: "12:00",
    submissionDeadline: new Date(),
    submissionTime: "12:00",
    openingDate: null as Date | null,
    openingTime: "12:00",
    requirePrebidMeeting: false,
    prebidMeetingDate: null as Date | null,
    prebidMeetingTime: "12:00",
    prebidMeetingVenue: "",
    requireRegistration: false,
    currency: "MVR" as "MVR" | "USD",
  });

  const [tenderItems, setTenderItems] = useState<TenderItemForm[]>([]);
  const [documentRequirements, setDocumentRequirements] = useState<DocumentRequirement[]>([]);
  const [evaluationCriteria, setEvaluationCriteria] = useState<EvaluationCriteria[]>([]);

  // Initialize form data when tender is loaded
  useEffect(() => {
    if (tender) {
      setFormData({
        description: tender.description || "",
        category: tender.category || "",
        publishingDate: tender.publishingDate ? new Date(tender.publishingDate) : new Date(),
        publishingTime: tender.publishingDate ? new Date(tender.publishingDate).toTimeString().slice(0, 5) : "12:00",
        submissionDeadline: tender.submissionDeadline ? new Date(tender.submissionDeadline) : new Date(),
        submissionTime: tender.submissionDeadline ? new Date(tender.submissionDeadline).toTimeString().slice(0, 5) : "12:00",
        openingDate: tender.openingDate ? new Date(tender.openingDate) : null,
        openingTime: tender.openingDate ? new Date(tender.openingDate).toTimeString().slice(0, 5) : "12:00",
        requirePrebidMeeting: tender.requirePrebidMeeting || false,
        prebidMeetingDate: tender.prebidMeetingDate ? new Date(tender.prebidMeetingDate) : null,
        prebidMeetingTime: tender.prebidMeetingDate ? new Date(tender.prebidMeetingDate).toTimeString().slice(0, 5) : "12:00",
        prebidMeetingVenue: tender.prebidMeetingVenue || "",
        requireRegistration: tender.requireRegistration || false,
        currency: tender.currency || "MVR",
      });
    }
  }, [tender]);

  // Initialize tender items when loaded
  useEffect(() => {
    if (existingItems) {
      setTenderItems(existingItems.map(item => ({
        id: item.id,
        itemName: item.itemName,
        description: item.description || "",
        quantity: item.quantity,
        unit: item.unit,
        order: item.order,
      })));
    }
  }, [existingItems]);

  // Initialize document requirements when loaded
  useEffect(() => {
    if (existingRequirements) {
      setDocumentRequirements(existingRequirements.map(req => ({
        id: req.id,
        documentType: req.documentType,
        documentCategory: req.documentCategory || 'GENERAL',
        description: req.description || "",
        isRequired: req.isRequired,
        order: req.order,
      })));
    }
  }, [existingRequirements]);

  // Initialize evaluation criteria when loaded
  useEffect(() => {
    if (existingCriteria) {
      setEvaluationCriteria(existingCriteria.map(criteria => ({
        id: criteria.id,
        criteriaName: criteria.criteriaName,
        description: criteria.description || "",
        criteriaType: criteria.criteriaType || 'GENERAL',
        weightage: Number(criteria.weightage),
        order: criteria.order,
        isDefault: criteria.isDefault,
      })));
    }
  }, [existingCriteria]);

  const addTenderItem = () => {
    const newItem: TenderItemForm = {
      id: Date.now().toString(),
      itemName: '',
      description: '',
      quantity: 1,
      unit: 'units',
      order: tenderItems.length + 1,
    };
    setTenderItems([...tenderItems, newItem]);
  };

  const removeTenderItem = (id: string) => {
    setTenderItems(tenderItems.filter(item => item.id !== id));
  };

  const updateTenderItem = (id: string, field: keyof TenderItemForm, value: any) => {
    setTenderItems(tenderItems.map(item => 
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addDocumentRequirement = () => {
    const newRequirement: DocumentRequirement = {
      id: Date.now().toString(),
      documentType: '',
      documentCategory: 'GENERAL',
      description: '',
      isRequired: true,
      order: documentRequirements.length + 1,
    };
    setDocumentRequirements([...documentRequirements, newRequirement]);
  };

  const removeDocumentRequirement = (id: string) => {
    setDocumentRequirements(documentRequirements.filter(req => req.id !== id));
  };

  const updateDocumentRequirement = (id: string, field: keyof DocumentRequirement, value: any) => {
    setDocumentRequirements(documentRequirements.map(req => 
      req.id === id ? { ...req, [field]: value } : req
    ));
  };

  const addEvaluationCriteria = () => {
    const newCriteria: EvaluationCriteria = {
      id: Date.now().toString(),
      criteriaName: '',
      description: '',
      criteriaType: 'GENERAL',
      weightage: 0,
      order: evaluationCriteria.length + 1,
      isDefault: false,
    };
    setEvaluationCriteria([...evaluationCriteria, newCriteria]);
  };

  const removeEvaluationCriteria = async (criteriaId: string) => {
    const criteria = evaluationCriteria.find(c => c.id === criteriaId);
    if (criteria && !criteria.isDefault) {
      // If it's an existing criteria (not a temp ID), delete it via API
      if (criteriaId && !criteriaId.startsWith('temp-') && criteriaId.length > 10) {
        try {
          await removeCriteria.mutateAsync({ tenderId: id!, criteriaId });
        } catch (error) {
          console.error('Error removing criteria:', error);
        }
      }
      setEvaluationCriteria(evaluationCriteria.filter(c => c.id !== criteriaId));
    } else if (criteria && criteria.isDefault) {
      notifications.show({
        title: "Cannot Remove",
        message: "Default criteria cannot be removed",
        color: "yellow",
      });
    } else {
      // New criteria that hasn't been saved yet
      setEvaluationCriteria(evaluationCriteria.filter(c => c.id !== criteriaId));
    }
  };

  const updateEvaluationCriteria = (id: string, field: keyof EvaluationCriteria, value: any) => {
    setEvaluationCriteria(evaluationCriteria.map(criteria => 
      criteria.id === id ? { ...criteria, [field]: value } : criteria
    ));
  };

  const saveEvaluationCriteria = async (): Promise<boolean> => {
    if (!id) return false;

    // Validate criteria
    const invalidCriteria = evaluationCriteria.filter(c => !c.criteriaName || c.weightage < 0 || c.weightage > 100);
    if (invalidCriteria.length > 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please provide names and valid weightage (0-100) for all evaluation criteria",
        color: "red",
      });
      return false;
    }

    // Validate total weightage equals 100
    const totalWeightage = evaluationCriteria.reduce((sum, criteria) => sum + Number(criteria.weightage), 0);
    if (evaluationCriteria.length > 0 && totalWeightage !== 100) {
      notifications.show({
        title: "Validation Error",
        message: "Total weightage for evaluation criteria must equal 100%",
        color: "red",
      });
      return false;
    }

    // Validate: Only one financial criteria allowed
    const financialCriteriaCount = evaluationCriteria.filter(c => c.criteriaType === 'FINANCIAL').length;
    if (financialCriteriaCount > 1) {
      notifications.show({
        title: "Validation Error",
        message: "Only one financial criteria is allowed per tender",
        color: "red",
      });
      return false;
    }

    // Save/update each criteria
    for (const criteria of evaluationCriteria) {
      const criteriaData = {
        criteriaName: criteria.criteriaName,
        description: criteria.description,
        criteriaType: criteria.criteriaType,
        weightage: criteria.weightage,
        order: criteria.order,
        isDefault: criteria.isDefault,
      };

      // Check if it's a new criteria (temporary ID) or existing
      // UUIDs are 36 characters long (with hyphens) or 32 without, and contain hex characters
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(criteria.id);
      const isTempId = criteria.id.startsWith('temp-');
      
      if (isUUID && !isTempId) {
        // Existing criteria with valid UUID - update
        try {
          await updateCriteria.mutateAsync({
            tenderId: id,
            criteriaId: criteria.id,
            data: criteriaData,
          });
        } catch (error) {
          console.error('Error updating criteria:', error);
          return false;
        }
      } else if (isTempId) {
        // New criteria with temp ID - create
        try {
          await createCriteria.mutateAsync({
            tenderId: id,
            data: criteriaData,
          });
        } catch (error) {
          console.error('Error creating criteria:', error);
          return false;
        }
      } else {
        // Invalid ID format - skip or log warning
        console.warn(`Skipping criteria with invalid ID format: ${criteria.id}`);
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.description || !formData.category) {
      notifications.show({
        title: "Validation Error",
        message: "Please fill in all required fields",
        color: "red",
      });
      return;
    }

    // Validate document requirements
    const invalidRequirements = documentRequirements.filter(req => !req.documentType);
    if (invalidRequirements.length > 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please select document types for all requirements",
        color: "red",
      });
      return;
    }

    // Validate custom documents have descriptions
    const customDocsWithoutDescription = documentRequirements.filter(
      req => req.documentType === 'CUSTOM' && !req.description?.trim()
    );
    if (customDocsWithoutDescription.length > 0) {
      notifications.show({
        title: "Validation Error",
        message: "Please provide descriptions for all custom documents",
        color: "red",
      });
      return;
    }

    try {
      // Helper function to combine date and time
      const combineDateTime = (date: Date, time: string) => {
        const [hours, minutes] = time.split(':').map(Number);
        const combined = new Date(date);
        combined.setHours(hours, minutes, 0, 0);
        return combined.toISOString();
      };

      // Save evaluation criteria first
      const criteriaSaved = await saveEvaluationCriteria();
      if (!criteriaSaved) {
        return; // Don't proceed if criteria validation failed
      }

      await updateTender.mutateAsync({
        id: id!,
        data: {
          description: formData.description,
          category: formData.category as "GOODS" | "SERVICES" | "WORKS",
          publishingDate: combineDateTime(formData.publishingDate, formData.publishingTime),
          submissionDeadline: combineDateTime(formData.submissionDeadline, formData.submissionTime),
          openingDate: formData.openingDate ? combineDateTime(formData.openingDate, formData.openingTime) : undefined,
          requirePrebidMeeting: formData.requirePrebidMeeting,
          prebidMeetingDate: formData.requirePrebidMeeting && formData.prebidMeetingDate 
            ? combineDateTime(formData.prebidMeetingDate, formData.prebidMeetingTime) 
            : undefined,
          prebidMeetingVenue: formData.requirePrebidMeeting ? formData.prebidMeetingVenue : undefined,
          requireRegistration: formData.requireRegistration,
          currency: formData.currency,
          documentRequirements: documentRequirements.map(req => ({
            documentType: req.documentType,
            documentCategory: req.documentCategory,
            description: req.description,
            isRequired: req.isRequired,
            order: req.order,
          })),
          items: tenderItems.map(item => ({
            itemName: item.itemName,
            description: item.description,
            quantity: item.quantity,
            unit: item.unit,
            order: item.order,
          })),
        },
      });

      notifications.show({
        title: "Success",
        message: "Tender updated successfully",
        color: "green",
      });

      navigate("/tenders");
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to update tender",
        color: "red",
      });
    }
  };

  if (loadingTender || loadingItems || loadingRequirements || loadingCriteria) {
    return (
      <Container size="lg" py="xl">
        <LoadingOverlay visible={true} />
      </Container>
    );
  }

  if (tenderError || !tender) {
    return (
      <Container size="lg" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error"
          color="red"
        >
          Failed to load tender. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Paper shadow="sm" p="xl">
        <form onSubmit={handleSubmit}>
          <Stack gap="xl">
            {/* Header */}
            <Group justify="space-between">
              <Group>
                <ActionIcon
                  variant="subtle"
                  onClick={() => navigate("/tenders")}
                  disabled={updateTender.isPending}
                >
                  <IconArrowLeft size={20} />
                </ActionIcon>
                <div>
                  <Title order={2}>Edit Tender</Title>
                  <Text c="dimmed" size="sm">
                    {tender.referenceNo}
                  </Text>
                </div>
              </Group>
            </Group>

            {/* Basic Information */}
            <Card shadow="sm" p="md">
              <Title order={4} mb="md">Basic Information</Title>
              
              <Stack gap="md">
                <Textarea
                  label="Description"
                  placeholder="Enter tender description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  rows={3}
                />

                <Select
                  label="Category"
                  placeholder="Select category"
                  data={[
                    { value: 'GOODS', label: 'Goods' },
                    { value: 'SERVICES', label: 'Services' },
                    { value: 'WORKS', label: 'Works' },
                  ]}
                  value={formData.category}
                  onChange={(value) => setFormData({ ...formData, category: value || '' })}
                  required
                />

                <Group grow>
                  <DatePickerInput
                    label="Publishing Date"
                    placeholder="Select publishing date"
                    value={formData.publishingDate}
                    onChange={(value: Date | null) =>
                      setFormData({ ...formData, publishingDate: value || new Date() })
                    }
                    required
                  />
                  <TimeInput
                    label="Publishing Time"
                    placeholder="Select time"
                    value={formData.publishingTime}
                    onChange={(e) => setFormData({ ...formData, publishingTime: e.target.value })}
                    required
                  />
                </Group>

                <Group grow>
                  <DatePickerInput
                    label="Submission Deadline"
                    placeholder="Select submission deadline"
                    value={formData.submissionDeadline}
                    onChange={(value: Date | null) =>
                      setFormData({ ...formData, submissionDeadline: value || new Date() })
                    }
                    required
                  />
                  <TimeInput
                    label="Submission Time"
                    placeholder="Select time"
                    value={formData.submissionTime}
                    onChange={(e) => setFormData({ ...formData, submissionTime: e.target.value })}
                    required
                  />
                </Group>

                <Group grow>
                  <DatePickerInput
                    label="Opening Date (Optional)"
                    placeholder="Select opening date"
                    value={formData.openingDate}
                    onChange={(value: Date | null) =>
                      setFormData({ ...formData, openingDate: value })
                    }
                    clearable
                  />
                  <TimeInput
                    label="Opening Time"
                    placeholder="Select time"
                    value={formData.openingTime}
                    onChange={(e) => setFormData({ ...formData, openingTime: e.target.value })}
                  />
                </Group>
              </Stack>
            </Card>

            {/* Pre-Bid Meeting Section */}
            <Card shadow="sm" p="md">
              <Title order={4} mb="md">Pre-Bid Meeting</Title>
              <Stack gap="md">
                <Checkbox
                  label="Require Pre-Bid Meeting"
                  description="If enabled, bidders must attend a pre-bid meeting before submitting their bids"
                  checked={formData.requirePrebidMeeting}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requirePrebidMeeting: e.target.checked,
                    })
                  }
                />
                {formData.requirePrebidMeeting && (
                  <Group grow>
                    <DatePickerInput
                      label="Pre-Bid Meeting Date"
                      placeholder="Select meeting date"
                      value={formData.prebidMeetingDate}
                      onChange={(value: Date | null) =>
                        setFormData({ ...formData, prebidMeetingDate: value })
                      }
                      required
                    />
                    <TimeInput
                      label="Pre-Bid Meeting Time"
                      placeholder="Select time"
                      value={formData.prebidMeetingTime}
                      onChange={(event) =>
                        setFormData({
                          ...formData,
                          prebidMeetingTime: event.target.value,
                        })
                      }
                      disabled={!formData.prebidMeetingDate}
                      required
                      type="default"
                    />
                  </Group>
                )}
                {formData.requirePrebidMeeting && (
                  <TextInput
                    label="Pre-Bid Meeting Venue"
                    placeholder="Enter meeting venue/location"
                    value={formData.prebidMeetingVenue}
                    onChange={(e) =>
                      setFormData({ ...formData, prebidMeetingVenue: e.target.value })
                    }
                    required
                  />
                )}
              </Stack>
            </Card>

            {/* Currency and Registration Section */}
            <Card shadow="sm" p="md">
              <Title order={4} mb="md">Bid Submission Settings</Title>
              <Stack gap="md">
                <Select
                  label="Currency"
                  description="Currency for bid amounts. Vendors must submit bids in this currency."
                  placeholder="Select currency"
                  data={[
                    { value: 'MVR', label: 'MVR (Maldivian Rufiyaa)' },
                    { value: 'USD', label: 'USD (US Dollar)' },
                  ]}
                  value={formData.currency}
                  onChange={(value) =>
                    setFormData({
                      ...formData,
                      currency: (value as "MVR" | "USD") || "MVR",
                    })
                  }
                  required
                />
                <Checkbox
                  label="Require Registration"
                  description="If enabled, vendors must register for this tender before submitting bids"
                  checked={formData.requireRegistration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requireRegistration: e.target.checked,
                    })
                  }
                />
              </Stack>
            </Card>

            <Divider label="TENDER ITEMS" labelPosition="center" />

            {/* Tender Items Section */}
            <Card shadow="sm" p="md">
              <Group justify="space-between" mb="md">
                <Title order={4}>Tender Items</Title>
                <Button
                  size="sm"
                  leftSection={<IconPlus size={16} />}
                  onClick={addTenderItem}
                >
                  Add Item
                </Button>
              </Group>

              <Text size="sm" c="dimmed" mb="md">
                Define the line items that bidders will quote on. Each item should have a clear description and estimated price.
              </Text>

              <Stack gap="md">
                {tenderItems.map((item) => (
                  <Card key={item.id} shadow="xs" p="md" withBorder>
                    <Group justify="space-between" mb="sm">
                      <Group gap="xs">
                        <IconGripVertical size={16} style={{ cursor: 'grab' }} />
                        <Badge size="sm" variant="light">
                          #{item.order}
                        </Badge>
                      </Group>
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => removeTenderItem(item.id)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>

                    <Stack gap="sm">
                      <TextInput
                        label="Item Name"
                        placeholder="Enter item name"
                        value={item.itemName}
                        onChange={(e) => updateTenderItem(item.id, 'itemName', e.target.value)}
                        required
                      />

                      <Textarea
                        label="Description"
                        placeholder="Enter item description"
                        value={item.description}
                        onChange={(e) => updateTenderItem(item.id, 'description', e.target.value)}
                        rows={2}
                      />

                      <Group grow>
                        <NumberInput
                          label="Quantity"
                          placeholder="Quantity"
                          value={item.quantity}
                          onChange={(value) => updateTenderItem(item.id, 'quantity', value || 1)}
                          min={1}
                        />
                        <Select
                          label="Unit"
                          placeholder="Select unit"
                          data={DEFAULT_UNITS}
                          value={item.unit}
                          onChange={(value) => updateTenderItem(item.id, 'unit', value || 'units')}
                        />
                      </Group>

                      <NumberInput
                        label="Order"
                        placeholder="Display order"
                        value={item.order}
                        onChange={(value) => updateTenderItem(item.id, 'order', value || 0)}
                        min={1}
                      />
                    </Stack>
                  </Card>
                ))}
              </Stack>

              {tenderItems.length === 0 && (
                <Text c="dimmed" ta="center" py="md">
                  No tender items added. Click "Add Item" to get started.
                </Text>
              )}
            </Card>

            <Divider label="REQUIRED DOCUMENTS" labelPosition="center" />

            {/* Document Requirements Section */}
            <Card shadow="sm" p="md">
              <Group justify="space-between" mb="md">
                <Title order={4}>Document Requirements</Title>
                <Button
                  size="sm"
                  leftSection={<IconPlus size={16} />}
                  onClick={addDocumentRequirement}
                >
                  Add Document
                </Button>
              </Group>

              <Text size="sm" c="dimmed" mb="md">
                Define the documents that bidders must submit with their bids. The standard requirements are pre-filled.
              </Text>

              <Stack gap="md">
                {documentRequirements.map((requirement) => (
                  <Card key={requirement.id} shadow="xs" p="md" withBorder>
                    <Group justify="space-between" mb="sm">
                      <Group gap="xs">
                        <IconGripVertical size={16} style={{ cursor: 'grab' }} />
                        <Badge size="sm" variant="light">
                          #{requirement.order}
                        </Badge>
                        {requirement.isRequired && (
                          <Badge size="sm" color="red">
                            Required
                          </Badge>
                        )}
                      </Group>
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => removeDocumentRequirement(requirement.id)}
                        disabled={documentRequirements.length <= 1}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>

                    <Stack gap="sm">
                      <Select
                        label="Document Type"
                        placeholder="Select document type"
                        data={DEFAULT_DOCUMENT_TYPES}
                        value={requirement.documentType}
                        onChange={(value) => updateDocumentRequirement(requirement.id, 'documentType', value || '')}
                        required
                      />

                      <Select
                        label="Document Category"
                        placeholder="Select category"
                        data={[
                          { value: 'FINANCIAL', label: 'Financial' },
                          { value: 'GENERAL', label: 'General' },
                          { value: 'TECHNICAL', label: 'Technical' },
                        ]}
                        value={requirement.documentCategory}
                        onChange={(value) => updateDocumentRequirement(requirement.id, 'documentCategory', value as 'FINANCIAL' | 'GENERAL' | 'TECHNICAL' || 'GENERAL')}
                        required
                      />

                      {requirement.documentType === 'CUSTOM' && (
                        <TextInput
                          label="Custom Document Title"
                          placeholder="Enter custom document title"
                          value={requirement.description}
                          onChange={(e) => updateDocumentRequirement(requirement.id, 'description', e.target.value)}
                          required
                        />
                      )}

                      {requirement.documentType !== 'CUSTOM' && (
                        <TextInput
                          label="Description (Optional)"
                          placeholder="Enter description for this document"
                          value={requirement.description}
                          onChange={(e) => updateDocumentRequirement(requirement.id, 'description', e.target.value)}
                        />
                      )}

                      <Group grow>
                        <NumberInput
                          label="Order"
                          placeholder="Display order"
                          value={requirement.order}
                          onChange={(value) => updateDocumentRequirement(requirement.id, 'order', value || 0)}
                          min={1}
                        />
                        <Checkbox
                          label="Required"
                          checked={requirement.isRequired}
                          onChange={(e) => updateDocumentRequirement(requirement.id, 'isRequired', e.target.checked)}
                        />
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </Stack>

              {documentRequirements.length === 0 && (
                <Text c="dimmed" ta="center" py="md">
                  No document requirements added. Click "Add Document" to get started.
                </Text>
              )}
            </Card>

            <Divider label="EVALUATION CRITERIA" labelPosition="center" />

            {/* Evaluation Criteria Section */}
            <Card shadow="sm" p="md">
              <Group justify="space-between" mb="md">
                <Title order={4}>Evaluation Criteria</Title>
                <Button
                  size="sm"
                  leftSection={<IconPlus size={16} />}
                  onClick={addEvaluationCriteria}
                >
                  Add Criteria
                </Button>
              </Group>

              <Text size="sm" c="dimmed" mb="md">
                Define the criteria by which bids will be evaluated. Each criterion should have a name, description, type, and weightage.
              </Text>

              <Stack gap="md">
                {evaluationCriteria.map((criteria) => (
                  <Card key={criteria.id} shadow="xs" p="md" withBorder>
                    <Group justify="space-between" mb="sm">
                      <Group gap="xs">
                        <IconGripVertical size={16} style={{ cursor: 'grab' }} />
                        <Badge size="sm" variant="light">
                          #{criteria.order}
                        </Badge>
                        {criteria.isDefault && (
                          <Badge size="sm" color="green">
                            Default
                          </Badge>
                        )}
                      </Group>
                      <ActionIcon
                        variant="light"
                        color="red"
                        onClick={() => removeEvaluationCriteria(criteria.id)}
                        disabled={criteria.isDefault || evaluationCriteria.length <= 1}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>

                    <Stack gap="sm">
                      <TextInput
                        label="Criteria Name"
                        placeholder="Enter criteria name"
                        value={criteria.criteriaName}
                        onChange={(e) => updateEvaluationCriteria(criteria.id, 'criteriaName', e.target.value)}
                        required
                      />

                      <Select
                        label="Criteria Type"
                        placeholder="Select type"
                        data={[
                          { value: 'FINANCIAL', label: 'Financial' },
                          { value: 'GENERAL', label: 'General' },
                        ]}
                        value={criteria.criteriaType}
                        onChange={(value) => {
                          // Check if trying to set FINANCIAL and another FINANCIAL already exists
                          if (value === 'FINANCIAL') {
                            const hasFinancial = evaluationCriteria.some(
                              c => c.id !== criteria.id && c.criteriaType === 'FINANCIAL'
                            );
                            if (hasFinancial) {
                              notifications.show({
                                title: "Validation Error",
                                message: "Only one financial criteria is allowed per tender",
                                color: "red",
                              });
                              return;
                            }
                          }
                          updateEvaluationCriteria(criteria.id, 'criteriaType', value as 'FINANCIAL' | 'GENERAL' || 'GENERAL');
                        }}
                        required
                      />

                      <Textarea
                        label="Description (Optional)"
                        placeholder="Enter description for this criteria"
                        value={criteria.description}
                        onChange={(e) => updateEvaluationCriteria(criteria.id, 'description', e.target.value)}
                      />

                      <Group grow>
                        <NumberInput
                          label="Weightage (%) - Total must be 100"
                          placeholder="Weightage"
                          value={criteria.weightage}
                          onChange={(value) => updateEvaluationCriteria(criteria.id, 'weightage', value || 0)}
                          min={0}
                          max={100}
                        />
                        <NumberInput
                          label="Order"
                          placeholder="Display order"
                          value={criteria.order}
                          onChange={(value) => updateEvaluationCriteria(criteria.id, 'order', value || 0)}
                          min={0}
                        />
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </Stack>

              {evaluationCriteria.length === 0 && (
                <Text c="dimmed" ta="center" py="md">
                  No evaluation criteria added. Click "Add Criteria" to get started.
                </Text>
              )}

              {evaluationCriteria.length > 0 && (
                <Alert color="blue" mt="md">
                  <Text size="sm">
                    Total Weightage: <strong>{evaluationCriteria.reduce((sum, c) => sum + Number(c.weightage), 0)}%</strong>
                    {evaluationCriteria.reduce((sum, c) => sum + Number(c.weightage), 0) !== 100 && (
                      <span style={{ color: 'red' }}> (Must equal 100%)</span>
                    )}
                  </Text>
                </Alert>
              )}
            </Card>

            {updateTender.isError && (
              <Alert
                icon={<IconAlertCircle size={16} />}
                title="Error"
                color="red"
              >
                Failed to update tender. Please try again.
              </Alert>
            )}

            <Group justify="flex-end" mt="xl">
              <Button
                variant="subtle"
                onClick={() => navigate("/tenders")}
                disabled={updateTender.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" loading={updateTender.isPending}>
                Update Tender
              </Button>
            </Group>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
}

